import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';

import { myProfile } from 'Redux/user-profile/selectors';
import { useClassroomInterruption } from 'Util/Hooks';
import { useGetFeedback } from 'Util/FeedBackService';
import { useNPSContentTracking } from 'Util/Analytics/useNPSContentTracking';
import {
  FeedbackImprovementAndSatisfiedContent,
  useNPSContentState,
  NPS_CONTENT_STATE,
} from 'Containers/Feedback';
import useClassroomVideoPlayer from 'Util/Hooks/useClassroomVideoPlayer';
import { VIDEO_STATE } from 'Context/constant';

/**
 * NPS content state (when skip(=) or complete(-) form)
 *    0 =====> 1 =====> 2 =====> ... ====> n
 *    |          ↘       ↓      ↙
 *    └----------->  COMPLETE
 */

const CourseFeedbackImprovementAndSatisfiedContentModal = ({
  shouldShowReview,
  progressToShow,
  userId,
}) => {
  const {
    isInterrupting,
    activateInterruption,
    deactivateInterruption,
    courseData: { courseCode, progressPercent },
  } = useClassroomInterruption();

  const { data: reviewData, loading: feedbackLoading } = useGetFeedback({
    templateId: 'IMPROVEMENT_SATISFIED_CONTENT_FEEDBACK',
    contentId: courseCode,
    userId,
  });
  const { videoState, hasPlayer } = useClassroomVideoPlayer();

  const [currentNPSState, updateNPSState] = useNPSContentState({ courseCode });
  const { logFeedbackSubmit, logFeedbackCancel, logFeedbackShow } = useNPSContentTracking();

  useEffect(() => {
    const shouldShow =
      Number.isInteger(currentNPSState) &&
      progressToShow.length > currentNPSState &&
      progressPercent >= progressToShow[currentNPSState];

    const shouldShowOnSubsectionComplete =
      shouldShowReview && (videoState === VIDEO_STATE.ENDED || !hasPlayer);

    const isReviewed =
      !!reviewData && Array.isArray(reviewData.getFeedback) && reviewData.getFeedback.length !== 0;

    if (
      shouldShowOnSubsectionComplete &&
      !feedbackLoading &&
      !isReviewed &&
      !isNil(currentNPSState) &&
      shouldShow
    ) {
      window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_INTERRUPT');
      activateInterruption();
    }
  }, [feedbackLoading, currentNPSState, progressPercent, shouldShowReview, videoState, reviewData]);

  useEffect(() => {
    if (isInterrupting) {
      logFeedbackShow(progressToShow[currentNPSState]);
    }
  }, [isInterrupting]);

  const handleSubmitFeedback = () => {
    logFeedbackSubmit(progressToShow[currentNPSState]);
    updateNPSState(NPS_CONTENT_STATE.COMPLETE);
    window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_CONTINUE');
    deactivateInterruption();
  };

  const handleCancelFeedback = () => {
    logFeedbackCancel(progressToShow[currentNPSState]);
    if (Number.isInteger(currentNPSState)) {
      let nextState = currentNPSState + 1;
      while (nextState < progressToShow.length && progressToShow[nextState] < progressPercent) {
        nextState++;
      }
      updateNPSState(nextState);
    }
    window.postMessage('VIDEO_ENDING_OVERLAY_MODAL_CONTINUE');
    deactivateInterruption();
  };

  return (
    <FeedbackImprovementAndSatisfiedContent
      isShow={isInterrupting}
      handleSubmitFeedback={handleSubmitFeedback}
      handleCancelFeedback={handleCancelFeedback}
      progressToShow={progressToShow}
      currentNPSState={currentNPSState}
    />
  );
};

CourseFeedbackImprovementAndSatisfiedContentModal.propTypes = {
  shouldShowReview: PropTypes.bool,
  progressToShow: PropTypes.arrayOf(PropTypes.number),
  userId: PropTypes.string,
};

export default connect(
  (state) => ({ userId: myProfile(state).id }),
  {}
)(CourseFeedbackImprovementAndSatisfiedContentModal);
